import { useEffect, useMemo, useRef, useState } from "react";
import CustomModal from "Components/Common/CustomModal/CustomModal";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useGcLayout } from "Pages/GcPrecon/GcLayout";
import Card from "../../../Card";
import SectionHeader from "../../../SectionHeader";
import {
  formatCurrency,
  parseCurrencyInput,
  isSOVLocked,
  type SovStatus,
} from "../constants/sovSetupData";
import ContractSummaryStrip from "../components/ContractSummaryStrip";
import QuickAddGrid from "../components/QuickAddGrid";
import SOVTable from "../components/SOVTable";
import SubmitBar from "../components/SubmitBar";
import SuccessScreen from "../components/SuccessScreen";
import TradeBreakdown from "../components/TradeBreakdown";
import VarianceAlert from "../components/VarianceAlert";
import {
  getAIA,
  createSOV,
  draftSOV,
  getSOVDraft,
  getSovVersions,
  getSovVersion,
  importSOVFromExcel,
  exportSOVCsv,
} from "services";
import { uploadFileToS3 } from "Pages/GcPrecon/GcPreconCommercial/UploadFileToS3";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import { useSovPdf, generatePdfBlob, sovPdfFileName } from "hooks/useSovPdf";
import Button from "Components/Common/Button/Button";

interface ContractAmount {
  amount: number;
  currency?: { symbol: string };
}

interface Lender {
  companyName?: string;
}

interface SovRow {
  id: string | number;
  aiaId?: string;
  csiCode: string;
  description: string;
  scheduledValue: number;
  vendor: string;
  vendorName?: string;
  vendorReadOnly?: boolean;
  status: string;
  colorHex?: string;
  isIncludeBudgetBreakdown?: boolean;
  isDeleted?: boolean;
}

interface AiaOption {
  id: string;
  code: string;
  label: string;
  name: string;
  colorHex?: string;
  isIncludeBudgetBreakdown?: boolean;
}

interface TradeBreakdownItem {
  label: string;
  value: number;
  pct: number;
  colorHex?: string;
}

interface SOVSetupPageProps {
  contractAmount?: ContractAmount;
  lender?: Lender;
  versionId?: string;
  projectId?: string;
  isStandalone?: boolean;
}

// sessionStorage keys shared with ManageSubContractor ("Add new subcontractor" round trip).
const SOV_RETURN_KEY = "sov_return_state";
const NEW_SUBCONTRACTOR_KEY = "sov_new_subcontractor";
const SOV_STASH_TTL_MS = 30 * 60 * 1000;

const getDivisionCode = (value: unknown): string => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  if (value && typeof value === "object") {
    const v = value as Record<string, unknown>;
    return String(v.code || v.value || v.id || v.itemCode || v.name || "");
  }
  return "";
};

export default function SOVSetupPage({
  contractAmount: contractAmountProp,
  lender: lenderProp,
  versionId,
  projectId: projectIdProp,
  isStandalone = false,
}: SOVSetupPageProps) {
  const { projectId: paramProjectId = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const projectId = projectIdProp || paramProjectId;
  const { setCrumbs = () => {} } = useGcLayout() ?? {};
  const { exportPdf, isExportingPdf } = useSovPdf();

  const sovData = JSON.parse(localStorage.getItem("gcSovData") || "{}");
  const lender: Lender = lenderProp ?? { companyName: sovData.bank ?? "" };

  const limit = 20;
  const [aiaPage, setAiaPage] = useState(1);
  const [aiaHasMore, setAiaHasMore] = useState(false);
  const [loadingMoreAia, setLoadingMoreAia] = useState(false);
  const fetchingAiaRef = useRef(false);
  const [screen, setScreen] = useState<"setup" | "success">("setup");
  const [rows, setRows] = useState<SovRow[]>([]);
  const [importMode, setImportMode] = useState("manual");
  const [toast, setToast] = useState("");
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isDrafting, setIsDrafting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [quickAddDivisions, setQuickAddDivisions] = useState<AiaOption[]>([]);
  const [loadingAia, setLoadingAia] = useState(true);
  const [sovStatus, setSovStatus] = useState<SovStatus>("");
  const [publishedVersionId, setPublishedVersionId] = useState("");
  const isLocked = isSOVLocked(sovStatus);
  const [deleteTargetId, setDeleteTargetId] = useState<string | number | null>(
    null,
  );
  const xlsxInputRef = useRef<HTMLInputElement | null>(null);
  const sovTableRef = useRef<HTMLDivElement>(null);
  const [apiProjectValue, setApiProjectValue] = useState<{
    amount: number;
  } | null>(null);

  const contractAmount: ContractAmount = {
    amount:
      apiProjectValue?.amount ??
      contractAmountProp?.amount ??
      sovData.contractAmount ??
      0,
    currency: contractAmountProp?.currency ?? {
      symbol: sovData.currencySymbol ?? "$",
    },
  };

  const total = useMemo(
    () => rows.reduce((sum, row) => sum + Number(row.scheduledValue || 0), 0),
    [rows],
  );

  const variance = useMemo(
    () => Math.round((total - (contractAmount?.amount ?? 0)) * 100) / 100,
    [total, contractAmount?.amount],
  );

  const tradeBreakdown = useMemo<TradeBreakdownItem[]>(() => {
    const base = contractAmount?.amount ?? 0;
    const result: TradeBreakdownItem[] = [];
    let otherTotal = 0;
    let hasOther = false;

    rows
      .filter((row) => row.description?.trim())
      .forEach((row) => {
        const value = row.scheduledValue || 0;
        if (row.isIncludeBudgetBreakdown !== false) {
          result.push({
            label: row.description,
            value,
            pct: base > 0 ? Math.round((value / base) * 100) : 0,
            colorHex: row.colorHex || "#14508c",
          });
        } else {
          hasOther = true;
          otherTotal += value;
        }
      });

    if (hasOther) {
      result.push({
        label: "All other trades",
        value: otherTotal,
        pct: base > 0 ? parseFloat(((otherTotal / base) * 100).toFixed(3)) : 0,
        colorHex: "#909399",
      });
    }

    return result;
  }, [rows, contractAmount?.amount]);

  const divisionsCount = useMemo(() => {
    const divisions = new Set(rows.map((row) => getDivisionCode(row.csiCode)));

    return divisions.size;
  }, [rows]);

  const addedDivisionCodes = useMemo(
    () =>
      new Set(rows.map((row) => getDivisionCode(row.csiCode)).filter(Boolean)),
    [rows],
  );

  const parseAndSetRows = (responseData: Record<string, unknown>) => {
    const sovItems =
      (responseData?.sovLienItems as Record<string, unknown>[]) || [];
    const budgetBreakdowns =
      (responseData?.budgetBreakdowns as Record<string, unknown>[]) || [];
    const colorByAiaItemId = Object.fromEntries(
      budgetBreakdowns.map((b) => [b.aiaItemId, b.colorHex]),
    );
    const includeBreakdownByAiaItemId = Object.fromEntries(
      budgetBreakdowns.map((b) => [
        b.aiaItemId,
        (b.aiaItem as { isIncludeBudgetBreakdown?: boolean })
          ?.isIncludeBudgetBreakdown ?? true,
      ]),
    );
    const formattedRows: SovRow[] = sovItems.map(
      (item: Record<string, unknown>) => ({
        id: (item.aiaItemId as string | number) ?? "",
        aiaId: item.aiaItemId as string,
        csiCode: item.aiaItemCode
          ? String(item.aiaItemCode)
          : (item.aiaItem as { itemCode?: string | number })?.itemCode
            ? String((item.aiaItem as { itemCode?: string | number }).itemCode)
            : "",
        description: (item.description as string) || "",
        scheduledValue: (item.amount as Record<string, number>)?.amount || 0,
        vendor: (item.subContractorId as string) || "",
        vendorName:
          ((item.subContractor as { companyName?: string })
            ?.companyName as string) || "",
        status: (item.status as string) || "new",
        colorHex:
          (colorByAiaItemId[item.aiaItemId as string] as string) ||
          (item.aiaItem as { colorHex?: string })?.colorHex ||
          "#14508c",
        isIncludeBudgetBreakdown:
          (includeBreakdownByAiaItemId[item.aiaItemId as string] as boolean) ??
          true,
        isDeleted: item.isDeleted as boolean | undefined,
      }),
    );
    setRows(formattedRows);
    setSovStatus((responseData.status as string) || "");
    setApiProjectValue(
      (responseData.projectValue as { amount: number }) ?? null,
    );
  };

  const getSovDetails = async (overrideVersionId?: string) => {
    setLoading(true);
    setError("");

    const resolvedVersionId =
      overrideVersionId || versionId || publishedVersionId;

    if (resolvedVersionId) {
      const { data, error } = await getSovVersion(projectId, resolvedVersionId);

      if (error) {
        setError((error as Error).message || "Failed to load SOV details.");
        setLoading(false);
        return;
      }
      if (data?.data) parseAndSetRows(data.data as Record<string, unknown>);
      setLoading(false);
      return;
    }

    // No known version ID — check if a published version already exists
    const { data: versionsData } = await getSovVersions(projectId);
    const versions: { id: string; versionNumber: number; status: string }[] =
      versionsData?.data ?? [];
    const activeVersion = [...versions]
      .filter((v) => v.status === "active")
      .sort((a, b) => b.versionNumber - a.versionNumber)[0];

    if (activeVersion) {
      setPublishedVersionId(activeVersion.id);
      const { data, error } = await getSovVersion(projectId, activeVersion.id);
      if (error) {
        setError((error as Error).message || "Failed to load SOV details.");
        setLoading(false);
        return;
      }
      if (data?.data) parseAndSetRows(data.data as Record<string, unknown>);
      setLoading(false);
      return;
    }

    // Truly new SOV — no version yet, use draft
    const { data, error } = await getSOVDraft(projectId);
    if (error) {
      setError((error as Error).message || "Failed to load SOV details.");
      setLoading(false);
      return;
    }
    if (data?.data) parseAndSetRows(data.data as Record<string, unknown>);
    setLoading(false);
  };

  // Coming back from "Add new subcontractor": restore the unsaved rows and select the
  // newly created subcontractor on the row that asked for it.
  const restoreStashedRows = () => {
    try {
      const raw = sessionStorage.getItem(SOV_RETURN_KEY);
      if (!raw) return;
      sessionStorage.removeItem(SOV_RETURN_KEY);
      const stash = JSON.parse(raw) as {
        projectId: string;
        rowId: string | number;
        rows: SovRow[];
        savedAt: number;
      };
      if (stash.projectId !== projectId) return;
      if (Date.now() - stash.savedAt > SOV_STASH_TTL_MS) return;
      const created = JSON.parse(
        sessionStorage.getItem(NEW_SUBCONTRACTOR_KEY) || "null",
      ) as { id: string; name: string } | null;
      sessionStorage.removeItem(NEW_SUBCONTRACTOR_KEY);
      const targetIndex = stash.rows.findIndex((r) => r.id === stash.rowId);
      setRows(
        stash.rows.map((r, i) =>
          created && i === targetIndex
            ? { ...r, vendor: created.id, vendorName: created.name }
            : r,
        ),
      );
    } catch {}
  };

  const handleAddSubcontractor = (rowId: string | number) => {
    try {
      sessionStorage.setItem(
        SOV_RETURN_KEY,
        JSON.stringify({ projectId, rowId, rows, savedAt: Date.now() }),
      );
    } catch {}
    navigate("/sub-contractor", {
      state: { returnTo: `${location.pathname}${location.search}` },
    });
  };

  useEffect(() => {
    if (!versionId) {
      setCrumbs([
        { label: "My Projects", link: "/gc-dashboard" },
        { label: "Schedule of Values" },
      ]);
    }
    getSovDetails().then(restoreStashedRows);
  }, [projectId, versionId]);

  useEffect(() => {
    if (!toast) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setToast("");
    }, 2600);

    return () => clearTimeout(timer);
  }, [toast]);

  const loadAiaPage = async (pageNum: number, reset: boolean) => {
    if (fetchingAiaRef.current) return;
    fetchingAiaRef.current = true;
    if (reset) {
      setLoadingAia(true);
      setError("");
    } else {
      setLoadingMoreAia(true);
    }
    const { data, error: apiError } = await getAIA(pageNum, limit);
    if (apiError) {
      if (reset) setError((apiError as Error).message || "Failed to load aia.");
      fetchingAiaRef.current = false;
      setLoadingAia(false);
      setLoadingMoreAia(false);
      return;
    }
    const aiaData: AiaOption[] = data!.data.map(
      (item: Record<string, unknown>) => ({
        id: item.id,
        code: getDivisionCode(item.itemCode),
        label: `${getDivisionCode(item.itemLabel)} - ${getDivisionCode(item.itemCode)}`,
        name: item.itemName,
        colorHex: (item.colorHex as string) || "#14508c",
        isIncludeBudgetBreakdown:
          (item.isIncludeBudgetBreakdown as boolean) ?? true,
      }),
    );
    const totalPages: number = data?.pagination?.totalPages ?? 1;
    setQuickAddDivisions((prev) => (reset ? aiaData : [...prev, ...aiaData]));
    setAiaHasMore(pageNum < totalPages);
    setAiaPage(pageNum);
    fetchingAiaRef.current = false;
    setLoadingAia(false);
    setLoadingMoreAia(false);
  };

  useEffect(() => {
    loadAiaPage(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadMoreAia = () => {
    if (fetchingAiaRef.current || !aiaHasMore) return;
    loadAiaPage(aiaPage + 1, false);
  };

  const showToast = (message: string) => setToast(message);

  const handleXlsxUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      setIsImporting(true);
      const uploadResult = await uploadFileToS3(file);
      const fileKey = uploadResult?.key;
      if (!fileKey)
        throw new Error("Upload succeeded but no file key returned");

      const { data, error } = await importSOVFromExcel(projectId, fileKey);
      if (error) throw new Error((error as Error).message || "Import failed");

      const excelRows = data?.data?.rows || [];
      const formattedRows: SovRow[] = excelRows.map(
        (item: Record<string, unknown>) => ({
          id: item.itemNo,
          aiaId: "",
          csiCode: "",
          description: (item.description as string) || "",
          scheduledValue: (item.scheduledValue as number) || 0,
          vendor: "",
          vendorName: "",
          status: "new",
          colorHex: "#14508c",
          isIncludeBudgetBreakdown: true,
        }),
      );
      setRows(formattedRows);
      showToast(`${formattedRows.length} line items imported from Excel`);
    } catch (err) {
      showToast((err as Error).message || "Upload failed");
    } finally {
      setIsImporting(false);
    }
  };

  const buildSOVPayload = () => {
    const budgetBreakdowns = rows
      .filter((row) => row.description.trim())
      .map((row) => ({
        aiaItemId: row.id,
        label: row.description || "Unlabeled",
        amount: {
          currencyCode: "USD",
          amount: row.scheduledValue,
        },
        amountPercentage:
          total > 0
            ? parseFloat(((row.scheduledValue / total) * 100).toFixed(3))
            : 0,
        colorHex: row.colorHex || "#14508c",
      }));

    const sovLienItems = rows.map((row, index) => ({
      displayOrder: index + 1,
      aiaItemId: row.id,
      aiaItemCode: row.csiCode,
      description: row.description,
      amount: {
        currencyCode: "USD",
        amount: row.scheduledValue,
      },
      amountPercentage:
        total > 0
          ? parseFloat(((row.scheduledValue / total) * 100).toFixed(3))
          : 0,
      subContractorId: row.vendor || null,
      status: "new",
    }));

    return {
      scheduledAmount: {
        currencyCode: "USD",
        amount: total,
      },
      scheduledVarianceAmount: variance,
      method: importMode === "upload" ? "upload" : "build",
      scheduledAmountCurrencyCode: "USD",
      lienItemsCount: rows.length,
      lienItemDivisions: divisionsCount,
      sovLienItems,
      budgetBreakdowns,
    };
  };

  const validateVendors = () => {
    const missing = rows.filter((row) => !row.vendor?.trim());
    if (missing.length > 0) {
      showToast(
        "Please select a subcontractor for all line items before proceeding",
      );
      setHighlightMissingVendors(true);
      setTimeout(() => {
        document
          .querySelector(".error-input")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return false;
    }
    return true;
  };

  const handleSaveDraft = async () => {
    if (!isVarianceZero) {
      showToast("SOV total must match the contract amount before saving draft");
      setHighlightVariance(true);
      sovTableRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }
    if (!validateVendors()) return;
    try {
      setIsDrafting(true);
      const payload = buildSOVPayload();
      const { data, error } = await draftSOV(projectId, payload);

      if (error) {
        showToast(
          `Error: ${(error as Error).message || "Failed to save draft"}`,
        );
        return;
      }

      if (data?.data?.status) setSovStatus(data.data.status);
      showToast("Draft saved successfully");
    } catch {
      showToast("Error saving draft");
    } finally {
      setIsDrafting(false);
    }
  };

  const handleExportCsv = async () => {
    if (!publishedVersionId) {
      showToast("Please save the SOV before exporting");
      return;
    }
    try {
      setIsExporting(true);
      await exportSOVCsv(projectId, publishedVersionId);
      showToast("CSV downloaded");
    } catch (err) {
      showToast((err as Error).message || "Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  const addRow = (payload: Partial<SovRow> & { id?: string | number } = {}) => {
    setRows((prev) => [
      ...prev,
      {
        id: payload.id ?? "",
        aiaId: (payload as { aiaId?: string }).aiaId || "",
        csiCode: payload.csiCode || "",
        description: payload.description || "",
        colorHex: payload.colorHex || "#14508c",
        isIncludeBudgetBreakdown: payload.isIncludeBudgetBreakdown ?? true,
        scheduledValue: 0,
        vendor: "",
        vendorName: "",
        status: "new",
      },
    ]);
  };

  const handleQuickAdd = (division: AiaOption) => {
    const divisionCode = getDivisionCode(division.code);

    if (!divisionCode) {
      showToast("Unable to add this AIA item right now");
      return;
    }

    if (addedDivisionCodes.has(divisionCode)) {
      showToast("Remove the line item in the table to remove this division");
      return;
    }

    addRow({
      id: division.id,
      csiCode: divisionCode,
      description: division.name,
      colorHex: division.colorHex || "#14508c",
      isIncludeBudgetBreakdown: division.isIncludeBudgetBreakdown ?? true,
    });
    showToast(`${divisionCode} added to SOV table`);
  };

  const handleDeleteRow = (rowId: string | number) => {
    setDeleteTargetId(rowId);
  };

  const confirmDeleteRow = () => {
    if (deleteTargetId !== null) {
      setRows((prev) => prev.filter((row) => row.id !== deleteTargetId));
    }
    setDeleteTargetId(null);
  };

  const handleMoveRow = (rowId: string | number, direction: number) => {
    setRows((prev) => {
      const index = prev.findIndex((row) => row.id === rowId);
      const targetIndex = index + direction;

      if (index < 0 || targetIndex < 0 || targetIndex >= prev.length) {
        return prev;
      }

      const nextRows = [...prev];
      const [moved] = nextRows.splice(index, 1);
      nextRows.splice(targetIndex, 0, moved);
      return nextRows;
    });
  };

  const handleChangeField = (
    rowId: string | number,
    field: string,
    value: string | number,
  ) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) {
          return row;
        }

        if (field === "scheduledValue") {
          return { ...row, scheduledValue: parseCurrencyInput(value) };
        }

        return { ...row, [field]: value };
      }),
    );
  };

  const handleSubmit = async () => {
    if (!isVarianceZero) {
      showToast("SOV total must match the contract amount before submitting");
      setHighlightVariance(true);
      sovTableRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }
    if (!validateVendors()) return;

    try {
      setIsSubmitting(true);

      const pdfData = {
        rows,
        contractAmount,
        total,
        variance,
        lineCount: rows.length,
        divisionsCount,
        sovStatus,
        tradeBreakdown,
        lender,
        projectName: sovData.projectName,
      };
      const fileName = sovPdfFileName(sovData.projectName);
      const blob = await generatePdfBlob(pdfData);
      const pdfFile = new File([blob], fileName, { type: "application/pdf" });
      const uploadResult = await uploadFileToS3(pdfFile);
      const pdfKey = uploadResult?.key;
      if (!pdfKey) throw new Error("PDF upload failed — no key returned");

      const payload = {
        ...buildSOVPayload(),
        sovPdf: { key: pdfKey, fileName },
      };
      const { data, error } = await createSOV(projectId, payload);
      if (error) {
        showToast(
          `Error: ${(error as Error).message || "Failed to submit SOV"}`,
        );
        return;
      }
      const createdVersionId = (data?.data?.versionId as string) || "";
      if (createdVersionId) setPublishedVersionId(createdVersionId);
      getSovDetails(createdVersionId || undefined);
      setScreen("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      showToast((err as Error).message || "Error submitting SOV");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isVarianceZero = Math.abs(variance) < 0.01;
  const [highlightVariance, setHighlightVariance] = useState(false);
  const [highlightMissingVendors, setHighlightMissingVendors] = useState(false);

  useEffect(() => {
    if (isVarianceZero) setHighlightVariance(false);
  }, [isVarianceZero]);

  useEffect(() => {
    if (highlightMissingVendors && rows.every((row) => row.vendor?.trim())) {
      setHighlightMissingVendors(false);
    }
  }, [rows, highlightMissingVendors]);
  const varianceText = `${variance > 0.01 ? "+" : ""}${formatCurrency(variance)}`;
  const varianceClass = isVarianceZero
    ? "ok"
    : Math.abs(variance) < 1000
      ? "amber"
      : "err";

  const steps = [
    { label: "Requirements Acknowledged", done: true },
    { label: "Pre-Con Documents", done: true },
    { label: "Schedule of Values", active: true },
    { label: "CSC Pre-Con Review" },
    { label: "Bank Approval" },
    { label: "First Draw Authorized" },
  ];

  return (
    <div>
      {!versionId && (
        <div className="sovStepper">
          {steps.map((step, i) => (
            <>
              <div
                key={i}
                className={`sovStep${step.done ? " done" : step.active ? " active" : ""}`}
              >
                <span className="sovStepBadge">
                  {step.done ? (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <circle cx="7" cy="7" r="7" fill="#1a7a4a" />
                      <path
                        d="M3.5 7l2.5 2.5 4.5-4.5"
                        stroke="#fff"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    i + 1
                  )}
                </span>
                <span className="sovStepLabel">{step.label}</span>
              </div>
              {i < steps.length - 1 && <span className="step-sep">›</span>}
            </>
          ))}
        </div>
      )}
      <div
        className={`sovSection${isStandalone ? " sovSection--standalone" : ""}`}
      >
        <div className="sovSetupRoot">
          {screen === "setup" ? (
            <>
              <SectionHeader
                title="Schedule of Values Setup"
                subtitle="Define the project budget as line items (AIA G703 Column B & C). These become the foundation for every draw request. Once submitted and approved by CSC and the bank, the SOV is locked — only approved change orders can modify it."
              />

              {loading ? (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: 12,
                    marginBottom: 16,
                  }}
                >
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} height={72} borderRadius={8} />
                  ))}
                </div>
              ) : (
                <>
                  <ContractSummaryStrip
                    contractAmount={contractAmount}
                    total={total}
                    variance={variance}
                    lineCount={rows.length}
                    divisionsCount={divisionsCount}
                    sovStatus={sovStatus}
                  />
                  <VarianceAlert total={total} variance={variance} />
                </>
              )}

              <Card
                title="Add by CSI Division"
                subtitle="Click to add a line item for that division"
              >
                <QuickAddGrid
                  divisions={quickAddDivisions}
                  addedCodes={addedDivisionCodes}
                  onToggle={handleQuickAdd}
                  disabled={isLocked}
                  loading={loadingAia}
                  hasMore={aiaHasMore}
                  loadingMore={loadingMoreAia}
                  onLoadMore={handleLoadMoreAia}
                />
              </Card>

              {loading ? (
                <>
                  <Skeleton
                    height={44}
                    borderRadius={6}
                    style={{ marginBottom: 8 }}
                  />
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton
                      key={i}
                      height={48}
                      borderRadius={6}
                      style={{ marginBottom: 6 }}
                    />
                  ))}
                  <Skeleton
                    height={200}
                    borderRadius={8}
                    style={{ marginTop: 16 }}
                  />
                </>
              ) : (
                <>
                  <div ref={sovTableRef}>
                    <SOVTable
                      onAddVendor={
                        localStorage.getItem("role") === "gc"
                          ? handleAddSubcontractor
                          : undefined
                      }
                      projectId={projectId}
                      rows={rows}
                      total={total}
                      contractAmount={contractAmount?.amount}
                      isPublished={isLocked}
                      aiaOptions={quickAddDivisions}
                      onAddRow={() => addRow()}
                      onExportCsv={handleExportCsv}
                      isExporting={isExporting}
                      onChangeField={handleChangeField}
                      onDelete={handleDeleteRow}
                      onMove={handleMoveRow}
                      highlightVariance={highlightVariance}
                      highlightMissingVendors={highlightMissingVendors}
                      sovStatus={sovStatus}
                    />
                  </div>
                  <Card title="Budget Breakdown by Trade">
                    <TradeBreakdown rows={tradeBreakdown} />
                  </Card>
                </>
              )}

              <div className="sovLockBanner">
                <span className="icon">🔒</span>
                <div>
                  <h4>SOV Locks After Approval</h4>
                  <p>
                    Once submitted, CSC and {lender?.companyName} review this
                    SOV. After approval, it becomes the basis for all AIA G703
                    draw requests. Any change must be processed as an approved
                    Change Order.
                  </p>
                </div>
              </div>

              <SubmitBar
                lineCount={rows.length}
                totalText={formatCurrency(total)}
                varianceText={varianceText}
                varianceClass={varianceClass}
                isPublished={isLocked}
                isDrafting={isDrafting}
                isSubmitting={isSubmitting}
                loading={loading}
                onSaveDraft={handleSaveDraft}
                onExportPdf={() =>
                  exportPdf({
                    rows,
                    contractAmount,
                    total,
                    variance,
                    lineCount: rows.length,
                    divisionsCount,
                    sovStatus,
                    tradeBreakdown,
                    lender,
                    projectName: sovData.projectName,
                  })
                }
                isExportingPdf={isExportingPdf}
                onSubmit={handleSubmit}
              />
            </>
          ) : (
            <SuccessScreen
              rows={rows}
              divisionsCount={divisionsCount}
              total={total}
              lender={lender}
              onBack={() => setScreen("setup")}
              onDownload={() =>
                exportPdf({
                  rows,
                  contractAmount,
                  total,
                  variance,
                  lineCount: rows.length,
                  divisionsCount,
                  sovStatus,
                  tradeBreakdown,
                  lender,
                  projectName: sovData.projectName,
                })
              }
            />
          )}

          <div className={`sovToast ${toast ? "show" : ""}`}>{toast}</div>

          <CustomModal
            show={deleteTargetId !== null}
            handleClose={() => setDeleteTargetId(null)}
            title="Remove Line Item"
            isFooterVisible
            footerContent={
              <>
                <Button
                  className="btn btn-secondary"
                  onClick={() => setDeleteTargetId(null)}
                >
                  Cancel
                </Button>
                <Button className="btn btn-danger" onClick={confirmDeleteRow}>
                  Remove
                </Button>
              </>
            }
          >
            <p>Are you sure you want to remove this line item from the SOV?</p>
          </CustomModal>
        </div>
      </div>
    </div>
  );
}
